import {
  createProductAttribute as createProductAttributeApi,
  createProductAttributeValue as createProductAttributeValueApi,
  deleteProductAttribute as deleteProductAttributeApi,
  deleteProductAttributeValue as deleteProductAttributeValueApi,
  getAllProductAttributes,
  getProductAttributeById,
  getProductAttributeValuesByAttributeId,
  restoreProductAttribute as restoreProductAttributeApi,
  restoreProductAttributeValue as restoreProductAttributeValueApi,
  searchProductAttributes,
  updateProductAttribute as updateProductAttributeApi,
  updateProductAttributeValue as updateProductAttributeValueApi,
} from "@/api/product-attribute-api";
import type { ProductAttributeSortOption } from "@/api/types";
import {
  ProductAttributeCreateRequest,
  ProductAttributeUpdateRequest,
  ProductAttributeValueCreateRequest,
  ProductAttributeValueUpdateRequest,
} from "@/api/types";
import {
  useCollectionData,
  useMutation,
  usePaginatedData,
} from "@/hooks/use-api";
import {
  AttributeValue,
  ProductAttribute,
  ProductAttributeCreateFormValues,
  ProductAttributeUpdateFormValues,
  ProductAttributeValueCreateFormValues,
  ProductAttributeValueUpdateFormValues,
} from "@/types/product-attribute-type";
import { mutate } from "swr";

// Constants
const PRODUCT_ATTRIBUTES_ENDPOINT = "/product-attributes";

// Hook for getting all product attributes with pagination
export function useProductAttributes() {
  const cacheKey = `${PRODUCT_ATTRIBUTES_ENDPOINT}`;

  let me = usePaginatedData(cacheKey, () => getAllProductAttributes(), {
    refreshInterval: 30000,
    revalidateOnFocus: true,
  });

  return me;
}

// Hook for getting a single product attribute
export function useProductAttribute(id: string | null) {
  const cacheKey = id ? `${PRODUCT_ATTRIBUTES_ENDPOINT}/${id}` : null;

  return useCollectionData(
    cacheKey,
    id
      ? () => getProductAttributeById(parseInt(id))
      : () => Promise.resolve({ data: null } as any),
    {
      refreshInterval: 0,
      revalidateOnFocus: true,
    }
  );
}

// Hook for searching product attributes
export function useSearchProductAttributes(
  searchTerm: string,
  options?: {
    page?: number;
    size?: number;
    sort?: ProductAttributeSortOption;
  }
) {
  const page = options?.page || 0;
  const size = options?.size || 20;
  const sort = options?.sort || ("name,asc" as ProductAttributeSortOption);

  const cacheKey = searchTerm
    ? `${PRODUCT_ATTRIBUTES_ENDPOINT}/search?term=${searchTerm}&page=${page}&size=${size}&sort=${sort}`
    : null;

  return usePaginatedData(
    cacheKey,
    searchTerm
      ? () => searchProductAttributes(searchTerm, page, size, sort)
      : () => Promise.resolve({ data: { content: [] } } as any),
    {
      refreshInterval: 0,
      revalidateOnFocus: false,
    }
  );
}

// Hook for getting attribute values by attribute ID
export function useProductAttributeValues(attributeId: string | null) {
  const cacheKey = attributeId
    ? `${PRODUCT_ATTRIBUTES_ENDPOINT}/${attributeId}/values`
    : null;

  return usePaginatedData(
    cacheKey,
    attributeId
      ? () =>
        getProductAttributeValuesByAttributeId(
          attributeId,
          0,
          100,
          "sortOrder,asc"
        )
      : () => Promise.resolve({ data: { content: [] } } as any),
    {
      refreshInterval: 30000,
      revalidateOnFocus: true,
    }
  );
}

// Hook for creating a product attribute
export function useCreateProductAttribute() {
  return useMutation(
    (data: ProductAttributeCreateFormValues) => {
      const requestData: ProductAttributeCreateRequest = {
        productId: data.productId,
        name: data.name,
        displayName: data.displayName,
        type: data.type,
        isActive: data.isActive,
      };
      return createProductAttributeApi(requestData);
    },
    {
      onSuccess: async (response, variables) => {
        console.log("Product attribute created successfully:", response.data);
        // Invalidate relevant cache keys
        await mutate(PRODUCT_ATTRIBUTES_ENDPOINT);
      },
      onError: (error) => {
        console.error("Error creating product attribute:", error);
      },
      invalidateKeys: [PRODUCT_ATTRIBUTES_ENDPOINT],
    }
  );
}

// Hook for updating a product attribute
export function useUpdateProductAttribute() {
  return useMutation(
    ({ id, data }: { id: string; data: ProductAttributeUpdateFormValues }) => {
      const requestData: ProductAttributeUpdateRequest = {
        name: data.name,
        displayName: data.displayName,
        type: data.type,
        isActive: data.isActive,
      };
      return updateProductAttributeApi(id, requestData);
    },
    {
      onSuccess: async (response, variables) => {
        console.log("Product attribute updated successfully:", response.data);
        // Invalidate relevant cache keys
        await Promise.all([
          mutate(PRODUCT_ATTRIBUTES_ENDPOINT),
          mutate(`${PRODUCT_ATTRIBUTES_ENDPOINT}/${variables.id}`),
        ]);
      },
      onError: (error) => {
        console.error("Error updating product attribute:", error);
      },
      invalidateKeys: [PRODUCT_ATTRIBUTES_ENDPOINT],
    }
  );
}

// Hook for deleting a product attribute
export function useDeleteProductAttribute() {
  return useMutation((id: string) => deleteProductAttributeApi(id), {
    onSuccess: async (response, variables) => {
      console.log("Product attribute deleted successfully:", response.data);
      // Invalidate relevant cache keys
      await Promise.all([
        mutate(PRODUCT_ATTRIBUTES_ENDPOINT),
        mutate(`${PRODUCT_ATTRIBUTES_ENDPOINT}/${variables}`),
      ]);
    },
    onError: (error) => {
      console.error("Error deleting product attribute:", error);
    },
    invalidateKeys: [PRODUCT_ATTRIBUTES_ENDPOINT],
  });
}

// Hook for restoring a product attribute
export function useRestoreProductAttribute() {
  return useMutation((id: string) => restoreProductAttributeApi(id), {
    onSuccess: async (response, variables) => {
      console.log("Product attribute restored successfully:", response.data);
      // Invalidate relevant cache keys
      await Promise.all([
        mutate(PRODUCT_ATTRIBUTES_ENDPOINT),
        mutate(`${PRODUCT_ATTRIBUTES_ENDPOINT}/${variables}`),
      ]);
    },
    onError: (error) => {
      console.error("Error restoring product attribute:", error);
    },
    invalidateKeys: [PRODUCT_ATTRIBUTES_ENDPOINT],
  });
}

// Hook for creating a product attribute value
export function useCreateProductAttributeValue() {
  return useMutation(
    (data: ProductAttributeValueCreateFormValues) => {
      const requestData: ProductAttributeValueCreateRequest = {
        attributeId: data.attributeId,
        value: data.value,
        displayValue: data.displayValue,
        colorCode: data.colorCode,
        sortOrder: data.sortOrder,
        isActive: true,
      };
      return createProductAttributeValueApi(requestData);
    },
    {
      onSuccess: async (response, variables) => {
        console.log(
          "Product attribute value created successfully:",
          response.data
        );
        // Invalidate relevant cache keys
        await Promise.all([
          mutate(
            `${PRODUCT_ATTRIBUTES_ENDPOINT}/${variables.attributeId}/values`
          ),
          mutate(PRODUCT_ATTRIBUTES_ENDPOINT),
        ]);
      },
      onError: (error) => {
        console.error("Error creating product attribute value:", error);
      },
      invalidateKeys: [PRODUCT_ATTRIBUTES_ENDPOINT],
    }
  );
}

// Hook for updating a product attribute value
export function useUpdateProductAttributeValue() {
  return useMutation(
    ({
      id,
      data,
      attributeId,
    }: {
      id: string;
      data: ProductAttributeValueUpdateFormValues;
      attributeId?: string;
    }) => {
      const requestData: ProductAttributeValueUpdateRequest = {
        value: data.value,
        displayValue: data.displayValue,
        colorCode: data.colorCode,
        sortOrder: data.sortOrder,
      };
      return updateProductAttributeValueApi(id, requestData);
    },
    {
      onSuccess: async (response, variables) => {
        console.log(
          "Product attribute value updated successfully:",
          response.data
        );
        // Invalidate relevant cache keys
        if (variables.attributeId) {
          await Promise.all([
            mutate(
              `${PRODUCT_ATTRIBUTES_ENDPOINT}/${variables.attributeId}/values`
            ),
            mutate(PRODUCT_ATTRIBUTES_ENDPOINT),
          ]);
        }
      },
      onError: (error) => {
        console.error("Error updating product attribute value:", error);
      },
      invalidateKeys: [PRODUCT_ATTRIBUTES_ENDPOINT],
    }
  );
}

// Hook for deleting a product attribute value
export function useDeleteProductAttributeValue() {
  return useMutation(
    ({ id, attributeId }: { id: string; attributeId?: string }) =>
      deleteProductAttributeValueApi(id),
    {
      onSuccess: async (response, variables) => {
        console.log(
          "Product attribute value deleted successfully:",
          response.data
        );
        // Invalidate relevant cache keys
        if (variables.attributeId) {
          await Promise.all([
            mutate(
              `${PRODUCT_ATTRIBUTES_ENDPOINT}/${variables.attributeId}/values`
            ),
            mutate(PRODUCT_ATTRIBUTES_ENDPOINT),
          ]);
        }
      },
      onError: (error) => {
        console.error("Error deleting product attribute value:", error);
      },
      invalidateKeys: [PRODUCT_ATTRIBUTES_ENDPOINT],
    }
  );
}

// Hook for restoring a product attribute value
export function useRestoreProductAttributeValue() {
  return useMutation(
    ({ id, attributeId }: { id: string; attributeId?: string }) =>
      restoreProductAttributeValueApi(parseInt(id)),
    {
      onSuccess: async (response, variables) => {
        console.log(
          "Product attribute value restored successfully:",
          response.data
        );
        // Invalidate relevant cache keys
        if (variables.attributeId) {
          await Promise.all([
            mutate(
              `${PRODUCT_ATTRIBUTES_ENDPOINT}/${variables.attributeId}/values`
            ),
            mutate(PRODUCT_ATTRIBUTES_ENDPOINT),
          ]);
        }
      },
      onError: (error) => {
        console.error("Error restoring product attribute value:", error);
      },
      invalidateKeys: [PRODUCT_ATTRIBUTES_ENDPOINT],
    }
  );
}

// Utility functions for product attribute operations
export const productAttributeService = {
  // Filter attributes by search query (client-side)
  filterAttributes: (
    attributes: ProductAttribute[],
    query: string
  ): ProductAttribute[] => {
    if (!query.trim()) return attributes;

    const lowerQuery = query.toLowerCase();
    return attributes.filter(
      (attribute) =>
        attribute.name.toLowerCase().includes(lowerQuery) ||
        attribute.displayName.toLowerCase().includes(lowerQuery) ||
        attribute.type.toLowerCase().includes(lowerQuery)
    );
  },

  // Get attribute statistics
  getAttributeStats: (attributes: ProductAttribute[]) => {
    const totalAttributes = attributes.length;
    const activeAttributes = attributes.filter((a) => a.isActive).length;
    const inactiveAttributes = totalAttributes - activeAttributes;

    const typeStats = attributes.reduce((acc, attribute) => {
      const type = attribute.type || "other";
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      totalAttributes,
      activeAttributes,
      inactiveAttributes,
      typeStats,
    };
  },

  // Format attribute for display
  formatAttribute: (attribute: ProductAttribute) => ({
    ...attribute,
    valueCount: attribute.attributeValues?.length || 0,
    isInUse: (attribute.attributeValues?.length || 0) > 0,
    typeDisplayName: attribute.type
      ? attribute.type.charAt(0).toUpperCase() + attribute.type.slice(1)
      : "Other",
  }),

  // Format attribute value for display
  formatAttributeValue: (value: AttributeValue) => ({
    ...value,
    hasColor: Boolean(value.colorCode && value.colorCode !== "#000000"),
    displayOrder: value.sortOrder || 0,
  }),

  // Generate attribute name from display name
  generateAttributeName: (displayName: string): string => {
    return displayName
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "");
  },

  // Validate attribute name uniqueness
  isAttributeNameUnique: (
    name: string,
    attributes: ProductAttribute[],
    excludeId?: string
  ): boolean => {
    return !attributes.some(
      (attr) =>
        attr.name.toLowerCase() === name.toLowerCase() && attr.id !== excludeId
    );
  },

  // Get attribute type display info
  getAttributeTypeInfo: (type: string) => {
    const typeMap = {
      color: {
        label: "Color",
        icon: "🎨",
        description: "Visual color variations",
      },
      size: {
        label: "Size",
        icon: "📏",
        description: "Physical size variations",
      },
      material: {
        label: "Material",
        icon: "🧵",
        description: "Material compositions",
      },
      style: { label: "Style", icon: "✨", description: "Style variations" },
      weight: { label: "Weight", icon: "⚖️", description: "Weight variations" },
      brand: { label: "Brand", icon: "🏷️", description: "Brand variations" },
      other: {
        label: "Other",
        icon: "📝",
        description: "Custom attribute type",
      },
    };

    return typeMap[type as keyof typeof typeMap] || typeMap.other;
  },
};
